/**
 * Token Refresh Service - Proactive token refresh
 * Refreshes access token 2 minutes before expiry to prevent interruption
 */

import { isTokenExpiredOrExpiringSoon, getAccessToken, isEvictedState } from './tokenManager';
import { refreshTokenFromCookie } from './getAuthToken';
import { getConnectionStatus, retryConnection } from './socketHandler';

let refreshInterval = null;
let visibilityHandler = null;
const REFRESH_BUFFER_SECONDS = 120; // 2 minutes before expiry
const CHECK_INTERVAL_MS = 60000; // Check every minute

/**
 * Refresh access token proactively
 * Delegates to the single shared refresh implementation (getAuthToken.js) so every
 * consumer in the app (proactive interval, on-demand thunks, axios interceptor)
 * awaits the same in-flight request instead of racing the single-use refresh
 * cookie against each other.
 */
const refreshAccessToken = async () => {
  const newToken = await refreshTokenFromCookie();
  if (newToken) {
    console.log('✅ Access token refreshed proactively');
    return true;
  }
  // Don't throw - let the interceptor handle it on next request
  return false;
};

/**
 * Check token freshness and refresh if missing/expiring soon.
 * Shared by the periodic interval and the tab-wake (visibilitychange) handler.
 */
const checkAndRefreshIfNeeded = () => {
  // This tab's session was forcibly evicted (signed in elsewhere) — don't
  // silently re-authenticate it via the still-valid refresh cookie. See
  // tokenManager.js's isEvicted flag for the full rationale.
  if (isEvictedState()) return;

  const token = getAccessToken();
  if (!token) {
    // No token - try to refresh using cookie
    refreshAccessToken();
    return;
  }

  // Check if token is expiring soon (within 2 minutes)
  if (isTokenExpiredOrExpiringSoon(REFRESH_BUFFER_SECONDS)) {
    console.log('⏰ Token expiring soon, refreshing proactively...');
    refreshAccessToken();
  }
};

/**
 * Fires when the tab regains visibility (e.g. laptop woken from sleep, or tab
 * switched back to). setInterval is suspended for the whole time a tab/OS is
 * asleep, so without this the token/socket only get checked on the next timer
 * tick (up to 60s later). Checking immediately on wake closes that gap.
 */
const handleVisibilityChange = () => {
  if (document.visibilityState !== 'visible') return;
  if (isEvictedState()) return; // don't auto-reconnect an evicted tab either

  checkAndRefreshIfNeeded();

  // The live socket is reliably dropped by OS sleep; nudge it back if needed
  // rather than waiting for the user to notice and click "Reconnect".
  if (!getConnectionStatus().isConnected) {
    console.log('🔄 Tab visible again and socket disconnected, retrying connection...');
    retryConnection();
  }
};

/**
 * Start proactive token refresh service
 * Checks every minute and refreshes token 2 minutes before expiry
 */
export const startProactiveTokenRefresh = () => {
  // Clear any existing interval/listener
  stopProactiveTokenRefresh();

  // Check token expiry every minute
  refreshInterval = setInterval(checkAndRefreshIfNeeded, CHECK_INTERVAL_MS);

  visibilityHandler = handleVisibilityChange;
  document.addEventListener('visibilitychange', visibilityHandler);

  console.log('🔄 Proactive token refresh service started');
};

/**
 * Stop proactive token refresh service
 */
export const stopProactiveTokenRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
    console.log('⏹️ Proactive token refresh service stopped');
  }
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
};

/**
 * Manually trigger token refresh (for testing or manual refresh)
 */
export const manualRefreshToken = async () => {
  return await refreshAccessToken();
};

